import { Bounds, Vector } from '@ardoq/graph';
import {
  AQBLOCKS_CONTEXT_STROKE,
  AQBLOCKS_CORNERCLAMP,
  AQBLOCKS_DROPSHADOW_FILL,
  AQBLOCKS_DROPSHADOW_OFFSET,
  AQBLOCKS_EXPANDER_SIZE,
  AQBLOCKS_FONT_FAMILY,
  AQBLOCKS_GROUPRD_SIZE,
  AQBLOCKS_LABEL_FONTSIZE,
  AQBLOCKS_LABEL_LINESIZE,
  AQBLOCKS_LEAFRD_SIZE,
  AQBLOCKS_LOWLIGHT,
  AQBLOCKS_SELECT_STROKE,
  AQBLOCKS_SUBLABEL_FONTSIZE,
  AQBLOCKS_SUBLABEL_LINESIZE,
  AQBLOCKS_TRANSITION_DURATION,
} from '../consts';

import { drawClosedNode } from './node.closed';
import { drawOpenNode } from './node.open';
import { AQBLOCKS_NODE_MARGIN, AQBLOCKS_PAD, AQBLOCKS_SEP } from '../consts';
import { BlocksNode, BlocksPlacementStyle, BlocksVisualState } from '../types';

import { currentTimestamp } from '@ardoq/date-time';
import {
  rectBloat,
  rectCenter,
  rectContains,
  rectHeight,
  rectWidth,
} from 'tabview/visualizations/misc/geometry';
import {
  hitTestNodeHandles,
  renderNodeHandles,
  renderNodeHandlesShadows,
} from './node.handles';

import {
  firstVisibleSublabel,
  hasVisualState,
  isGroup,
  isNodeOpen,
  isNodeRoot,
  isNodeSynthetic,
} from '../util';
import { clamp, sum } from 'lodash';
import { NodeHit, RenderContext } from './types';
import { drawFontAwesomeIcon } from './utils';
import { DragOperation } from '../interactions/types';
import {
  getDisplayLabelHeight,
  getDisplaySublabelsHeight,
} from './node.composition';
import { wrapString } from 'tabview/visualizations/misc/string';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import { darkenBy } from './rendercontext';

const margin = AQBLOCKS_NODE_MARGIN;
const pad = AQBLOCKS_PAD;
const sep = AQBLOCKS_SEP;

//const approxLabelMeasure = (str: string) => str.length * 0.7 * AQBLOCKS_LABEL_FONTSIZE;
const canvasLabelMeasure = canvasLabelMeasurer(
  `${AQBLOCKS_LABEL_FONTSIZE}px ${AQBLOCKS_FONT_FAMILY}`
);

//const approxSublabelMeasure = (str: string) => str.length * 0.7 * AQBLOCKS_SUBLABEL_FONTSIZE;
const canvasSublabelMeasure = canvasLabelMeasurer(
  `${AQBLOCKS_SUBLABEL_FONTSIZE}px ${AQBLOCKS_FONT_FAMILY}`
);

const labelMeasure = canvasLabelMeasure;
const labelWidth = (labels: string[], width: number) => {
  const lines = wrapString(labels[0], width, labelMeasure, 3);

  let ret = 0;

  for (const line of lines) {
    ret = Math.max(ret, labelMeasure(line));
  }

  return ret;
};
const labelHeight = (labels: string[], width: number) => {
  const lines = wrapString(labels[0], width, labelMeasure, 3);

  return lines.length * AQBLOCKS_LABEL_LINESIZE;
};

const sublabelMeasure = canvasSublabelMeasure;
const sublabelsWidth = (node: BlocksNode, widthMax: number) => {
  let ret = 0;
  const { labels } = node;
  const first = firstVisibleSublabel(node);

  for (let i = first; i < labels.length; ++i) {
    const label = labels[i];
    const lines = wrapString(label, widthMax, sublabelMeasure, 3);

    for (const line of lines) {
      ret = Math.max(ret, sublabelMeasure(line));
    }
  }

  return ret;
};
const sublabelsHeight = (node: BlocksNode, width: number) => {
  let ret = 0;
  const { labels } = node;
  let sublabelsCount = 0;
  const first = firstVisibleSublabel(node);

  for (let i = first; i < labels.length; ++i) {
    const label = labels[i];
    const lines = wrapString(label, width, sublabelMeasure, 3);

    if (lines.length > 0) {
      ret += lines.length * AQBLOCKS_SUBLABEL_LINESIZE;
      ++sublabelsCount;
    }
  }

  if (sublabelsCount > 0) {
    ret += (sublabelsCount - 1) * sep;
  }

  return ret;
};

export const getWrappedNodeLabels = (node: BlocksNode, colWidth: number) => {
  const displayLabels: string[][] = [];
  const width = colWidth - 2 * AQBLOCKS_PAD;
  const labelWidth =
    isGroup(node) && isNodeOpen(node)
      ? width -
        (AQBLOCKS_EXPANDER_SIZE + AQBLOCKS_GROUPRD_SIZE + 2 * AQBLOCKS_SEP)
      : width;

  displayLabels.push(wrapString(node.labels[0], labelWidth, labelMeasure, 3));

  for (let i = 1; i < node.labels.length; ++i) {
    const label = node.labels[i];
    const displayLabel = label
      ? wrapString(label, width, sublabelMeasure, 3)
      : [];

    displayLabels.push(displayLabel);
  }

  return displayLabels;
};

export const getNodeMinimumCellWidth = (
  node: BlocksNode,
  placementStyle: BlocksPlacementStyle
) => {
  // extension defines the fraction by which width can be extended beyond minWidth.
  // setting it to one prevents extension. this constant is local to this function
  // and has no business being tuned elsewhere
  const exension = 1;
  const widthMin = placementStyle.nodeMinimumSize[0];
  const widthMax = exension * placementStyle.nodeMinimumSize[0];
  let width = NaN;

  const label = labelWidth(node.labels, widthMax);
  const sublabel = sublabelsWidth(node, widthMax);

  // synthetic groups don't display labels

  if (isGroup(node) && isNodeOpen(node)) {
    const expander = AQBLOCKS_EXPANDER_SIZE;
    const rd = AQBLOCKS_GROUPRD_SIZE;
    const header = expander + sep + rd + sep + label; // header line width

    width = Math.max(header, sublabel);
  } else {
    const rd = AQBLOCKS_LEAFRD_SIZE;

    width = Math.max(rd, label, sublabel);
  }

  return Math.max(width, widthMin) + 2 * (margin + pad);
};

export const getNodeMinimumCellHeight = (
  node: BlocksNode,
  cellWidth: number,
  placementStyle: BlocksPlacementStyle
) => {
  const margin = AQBLOCKS_NODE_MARGIN;
  const pad = AQBLOCKS_PAD;
  const sep = AQBLOCKS_SEP;
  const width = Math.floor(cellWidth - 2 * (margin + pad));
  const labelWidth =
    isGroup(node) && isNodeOpen(node)
      ? width -
        (AQBLOCKS_EXPANDER_SIZE + AQBLOCKS_GROUPRD_SIZE + 2 * AQBLOCKS_SEP)
      : width;

  const heightMin = placementStyle.nodeMinimumSize[1];

  let height = NaN;

  const label = labelHeight(node.labels, labelWidth);
  const sublabels = sublabelsHeight(node, width);

  if (isGroup(node) && isNodeOpen(node)) {
    const expander = AQBLOCKS_EXPANDER_SIZE;
    const rd = AQBLOCKS_GROUPRD_SIZE;
    const header = Math.max(expander, rd, label);
    const content = node.rowSize ? sum(node.rowSize) : 0;

    height = header + sep + sublabels + (sublabels > 0 ? sep : 0) + content;
  } else {
    const expander = AQBLOCKS_EXPANDER_SIZE;
    const header = isGroup(node) ? expander : 0;
    const rd = AQBLOCKS_LEAFRD_SIZE;

    height =
      header +
      (header > 0 ? sep + sep : 0) +
      (rd + sep + label) +
      (sublabels > 0 ? sep : 0) +
      sublabels;
  }

  return Math.max(height, heightMin) + 2 * (margin + pad);
};

export const setNodeCellRect = (node: BlocksNode, cell: Bounds) => {
  if (!node.animation) {
    node.animation = {
      begin: currentTimestamp(),
      duration: AQBLOCKS_TRANSITION_DURATION,
      from: node.cell ? node.cell : null,
    };
  }

  node.cell = [...cell];
  node.bounds = rectBloat(node.cell, -AQBLOCKS_NODE_MARGIN);

  if (isNodeSynthetic(node)) {
    node.content = [...node.bounds];
  } else if (isGroup(node)) {
    const padded = rectBloat(node.bounds, -AQBLOCKS_PAD);
    const top =
      padded[1] +
      getDisplayLabelHeight(node) +
      AQBLOCKS_SEP +
      getDisplaySublabelsHeight(node) +
      AQBLOCKS_SEP;

    node.content = [padded[0], top, padded[2], padded[3]];
  } else {
    node.content = undefined;
  }
};

export const hitTestNode = (
  node: BlocksNode,
  pos: Vector,
  resolution: number
) => {
  let ret = NodeHit.None;

  if (hasVisualState(node, BlocksVisualState.Select)) {
    const hitHandles = hitTestNodeHandles(node.bounds, pos, resolution);

    ret = ret | (hitHandles & DragOperation.Left ? NodeHit.Left : 0);
    ret = ret | (hitHandles & DragOperation.Top ? NodeHit.Top : 0);
    ret = ret | (hitHandles & DragOperation.Right ? NodeHit.Right : 0);
    ret = ret | (hitHandles & DragOperation.Bottom ? NodeHit.Bottom : 0);
  }

  if (!rectContains(node.bounds, pos)) {
    return ret;
  }

  ret = ret | NodeHit.Body;

  if (isGroup(node) && !isNodeSynthetic(node)) {
    const bounds = node.bounds;
    const pad = AQBLOCKS_PAD;
    const expander = AQBLOCKS_EXPANDER_SIZE;
    const rc: Bounds = [
      bounds[0] + pad,
      bounds[1] + pad,
      bounds[0] + pad + expander,
      bounds[1] + pad + expander,
    ];

    if (rectContains(rc, pos)) {
      ret = ret | NodeHit.Expand;
    }
  }

  return ret;
};

export const renderNode = (
  node: BlocksNode,
  cell: Bounds,
  globalAlpha: number,
  params: RenderContext
): boolean => {
  let done = true;

  if (hasVisualState(node, BlocksVisualState.Hidden)) {
    return done;
  }

  if (isNodeRoot(node)) {
    return done;
  }

  const { projection, ctx } = params;

  const isHighlight = hasVisualState(node, BlocksVisualState.Highlight);
  const isLowlight = params.isLowlight && !isHighlight;
  const isHover = hasVisualState(node, BlocksVisualState.Hover);
  const isSelect = hasVisualState(node, BlocksVisualState.Select);
  const isEditable =
    !params.isReadonly && true; /* node is individually editable */
  const isContext = node.isContext;
  const isEdited = node.children && node.layoutType;
  const isSynthetic = isNodeSynthetic(node);

  /* positions and sizes */
  const bounds =
    cell; /* get this from some damn place; the bounds don't necessarily fill the cell */
  const rc = projection.projectBoundsSnapped(bounds);
  const rcWidth = rectWidth(rc);
  const rcHeight = rectHeight(rc);
  const rcSize = Math.max(rcWidth, rcHeight);
  const borderSize =
    isSelect && !isEditable ? params.selectBorderSize : params.borderSize;
  const cornerRadius = clamp(
    0.25 * rcSize,
    AQBLOCKS_CORNERCLAMP[0],
    params.cornerRadius
  );
  const dropshadowOffset = AQBLOCKS_DROPSHADOW_OFFSET + 0.5 * borderSize;

  /* colors */
  const color = node.color;
  const fillColor = isHover
    ? params.nodeHoverFill(node)
    : params.nodeFill(node);
  const borderColor = isSelect
    ? AQBLOCKS_SELECT_STROKE
    : params.nodeBorder(node);

  const isScaleAnimation = isHighlight && !params.prefersReducedMotion;

  if (isScaleAnimation && rcSize > 0) {
    const center = rectCenter(rc);
    const p = currentTimestamp() / AQBLOCKS_TRANSITION_DURATION;
    const scale = 1 + (10 / rcSize) * Math.sin(Math.PI * 2.0 * p);

    ctx.save();
    ctx.translate(center[0], center[1]);
    ctx.scale(scale, scale);
    ctx.translate(-center[0], -center[1]);

    done = false;
  }

  ctx.globalAlpha = globalAlpha * (isLowlight ? AQBLOCKS_LOWLIGHT : 1);

  if (isLowlight) {
    //ctx.filter = 'blur(2px)'
    ctx.globalAlpha = globalAlpha * AQBLOCKS_LOWLIGHT;
  } else {
    //ctx.filter = 'none';
    ctx.globalAlpha = globalAlpha;
  }

  /* hover drop shadow */

  if (isHover) {
    ctx.beginPath();
    ctx.roundRect(
      rc[0] + dropshadowOffset,
      rc[1] + dropshadowOffset,
      rcWidth,
      rcHeight,
      cornerRadius + dropshadowOffset
    );
    ctx.fillStyle = AQBLOCKS_DROPSHADOW_FILL;
    ctx.fill();
  }

  /* select handles dropshadows before the node */

  if (isSelect && isEditable) {
    renderNodeHandlesShadows(rc, params);
  }

  /* draw node fill */

  if (!isSynthetic || isHover) {
    ctx.beginPath();
    if (cornerRadius > AQBLOCKS_CORNERCLAMP[0]) {
      ctx.roundRect(rc[0], rc[1], rcWidth, rcHeight, cornerRadius);
    } else {
      ctx.roundRect(rc[0], rc[1], rcWidth, rcHeight);
    }

    if (isHover) {
      const gradient = ctx.createLinearGradient(rc[0], rc[1], rc[0], rc[3]);
      gradient.addColorStop(0, fillColor);
      gradient.addColorStop(1, darkenBy(0.07, fillColor));
      ctx.fillStyle = gradient;
    } else {
      ctx.fillStyle = fillColor;
    }

    ctx.fill();
  }

  /* draw node content */

  if (isGroup(node) && isNodeOpen(node)) {
    drawOpenNode(node, bounds, color, fillColor, params);
  } else {
    drawClosedNode(node, bounds, color, fillColor, params);
  }

  /* draw node border */

  if (!isSynthetic || isHover || isSelect) {
    ctx.beginPath();

    if (cornerRadius > AQBLOCKS_CORNERCLAMP[0]) {
      ctx.roundRect(rc[0], rc[1], rcWidth, rcHeight, cornerRadius);
    } else {
      ctx.roundRect(rc[0], rc[1], rcWidth, rcHeight);
    }

    ctx.lineWidth = borderSize;
    ctx.strokeStyle = borderColor;
    ctx.stroke();
  }

  if (isEditable && isEdited) {
    const rcEdited: Bounds = [
      bounds[2] - 25,
      bounds[1] - 25,
      bounds[2] + 25,
      bounds[1] + 25,
    ];

    drawFontAwesomeIcon('cog', rcEdited, fillColor, params);
  }

  if (isContext) {
    /* draw node context rect */
    const sz = 4; // context bloat

    ctx.lineWidth = 4;
    ctx.strokeStyle = AQBLOCKS_CONTEXT_STROKE;
    ctx.beginPath();
    ctx.roundRect(
      rc[0] - sz,
      rc[1] - sz,
      rcWidth + 2 * sz,
      rcHeight + 2 * sz,
      2 * cornerRadius
    );
    ctx.stroke();
  }

  if (isSelect && isEditable) {
    renderNodeHandles(rc, params);
  }

  if (isScaleAnimation) {
    ctx.restore();
  }

  return done;
};
